import { escapeHtml } from '../util/escapeHtml.js';
import { mountShell } from '../components/Shell.js';
import { autoCollapseSiblings } from '../util/collapsible.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import type {
  CharacterDto,
  HouseDetailDto,
  InventoryItemDto,
  LoadoutDto,
  ShipDto,
  OwnedDomicileDto,
  AwardDto,
  AccountSummary,
  CareerStatsDto,
  ActivityEntryDto,
  RefillStatusDto,
  PerkProgressDto,
  GatheringSkillSummaryDto,
} from '@rex-mortis/shared';
import { PERK_CATALOG, perkName } from '@rex-mortis/shared';
import { EQUIP_PICKER_CSS, slotPickerHtml, wireSlotPickers, type SlotChangeResult } from '../util/equipSlots.js';
import { flashToast } from '../util/toast.js';
import { newArrivalPill } from '../util/newArrivalBadge.js';
import { attachPerkInfoToggle } from '../components/PerkInfoPanel.js';
import { getTitleDef, customTitleCss, customChipCss, titleChipHtml } from '../components/Title.js';
import { STATUS_GLYPH } from '../components/Shell.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { formatBigNum, formatBigStr as formatBigStrShared, formatCrowns } from '../util/formatBig.js';
import { craftProgress } from '../util/craft.js';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

interface ProfileData {
  account: AccountSummary;
  character: CharacterDto;
  loadout: LoadoutDto | null;
  inventory: InventoryItemDto[];
  house: HouseDetailDto | null;
  domicile: OwnedDomicileDto | null;
  activeShip: ShipDto | null;
  awards: AwardDto[];
  career: CareerStatsDto | null;
  activity: ActivityEntryDto[];
  refillStatus: RefillStatusDto | null;
  perks: PerkProgressDto[];
  gatheringSkills: GatheringSkillSummaryDto[];
  /** Active Shade-wound from /me, if any. */
  wound: { pct: number; healsAt: string } | null;
}

export async function renderProfile(root: HTMLElement): Promise<void> {
  let data: ProfileData | null = null;
  let flash: FlashState | null = null;

  async function refresh(): Promise<void> {
    try {
      const me = await api.me();
      store.set({ account: me.account, character: me.character });

      // Best-effort fetches — Profile should still render even if a side call fails.
      const [loadoutR, invR, houseR, domR, shipR, awardsR, careerR, activityR, refillR, perksR, gatherR] = await Promise.allSettled([
        api.getLoadout(),
        api.listInventory(),
        api.myHouse(),
        api.myDomicile(),
        api.listShips(),
        api.listAwards(),
        api.getCareerStats(),
        api.getActivity(15),
        api.getRefillStatus(),
        api.getPerks(),
        api.getGatheringSkills(),
      ]);

      const loadout = loadoutR.status === 'fulfilled' ? loadoutR.value.loadout : null;
      const inventory = invR.status === 'fulfilled' ? invR.value.items : [];
      const house = houseR.status === 'fulfilled' ? houseR.value.house : null;
      const domicile = domR.status === 'fulfilled' ? domR.value.domicile : null;
      const activeShip =
        shipR.status === 'fulfilled'
          ? (shipR.value.ships.find((s) => s.active) ?? null)
          : null;
      const awards = awardsR.status === 'fulfilled' ? awardsR.value.awards : [];
      const career = careerR.status === 'fulfilled' ? careerR.value.stats : null;
      const activity = activityR.status === 'fulfilled' ? activityR.value.entries : [];
      const refillStatus = refillR.status === 'fulfilled' ? refillR.value.status : null;
      const perks = perksR.status === 'fulfilled' ? perksR.value.perks : [];
      const gatheringSkills = gatherR.status === 'fulfilled' ? gatherR.value.skills : [];

      data = {
        account: me.account,
        character: me.character,
        loadout,
        inventory,
        house,
        domicile,
        activeShip,
        awards,
        career,
        activity,
        refillStatus,
        perks,
        gatheringSkills,
        wound: me.darkRaid?.wound ?? null,
      };
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Failed to load profile.';
      flash = { kind: 'danger', message: msg };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    if (!data) {
      main.innerHTML = `
        <img class="page-banner" src="/img/banners/profile.webp" alt="" />
        <h1>Profile</h1>
        <div class="alert danger">${flash?.message ?? 'Could not load profile.'}</div>
      `;
      return;
    }
    renderProfilePanels(main, data);
  });
}

/** "2h 13m" style countdown to an ISO timestamp (floor 1m). */
function healsIn(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return 'any moment';
  const total = Math.ceil(ms / 60000);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** A battle-stat cell — shows the effective (wounded) value beside the true one. */
function battleStatCell(value: number, woundPct: number): string {
  const base = formatStat(value);
  if (woundPct <= 0) return base;
  const eff = formatStat(Math.round(value * (1 - woundPct / 100)));
  return `${base} <span class="wound-eff" style="color:var(--c-oxblood); font-weight:700;" title="Effective in combat while the Shade-wound lasts">→ ${eff}</span>`;
}

function renderProfilePanels(main: HTMLElement, d: ProfileData): void {
  const c = d.character;
  const totalAwards = d.awards.length;
  const achievedAwards = d.awards.filter((a) => a.achieved).length;
  const woundPct = d.wound?.pct ?? 0;

  // Battle-stat power index (sum of four — also displayed individually)
  const battlePower = c.strength + c.defense + c.speed + c.dexterity;
  // Working power
  const workingTotal = c.toil + c.wits + c.endurance;

  const statusLabel = (() => {
    if (c.status === 'OKAY') return 'Hale';
    if (c.status === 'INFIRMARY') return 'In Infirmary';
    if (c.status === 'DUNGEON') return 'In the Dungeon';
    if (c.status === 'TRAVELLING') return 'On the road';
    return c.status;
  })();

  main.innerHTML = `
    <h1>Profile</h1>
    <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
      A page of arms and bearings.
    </p>
    ${renderIdentityBlock(c, d.account.membershipFlairColour ?? null)}

    <section class="panel">
      <div style="display:flex; flex-wrap:wrap; align-items: baseline; gap: var(--s-3);">
        ${newArrivalPill(c.protectionEndsAt)}
        <span class="pill gold">Level ${c.level}</span>
        ${d.account.soulNumber ? `<span class="pill" title="Your registration number — Soul #${d.account.soulNumber} to join the realm. Lower is earlier.">Soul #${formatBigNum(d.account.soulNumber)}</span>` : ''}
        <span class="pill">XP: ${formatBigStr(c.xp)}</span>
        <span class="pill">Status: <span style="font-size: 1.1em; margin-right: 4px;">${STATUS_GLYPH[c.status] ?? ''}</span><strong>${escapeHtml(statusLabel)}</strong></span>
        <span class="pill">Location: <strong>${escapeHtml(formatLocation(c.location))}</strong></span>
        ${d.wound ? `<span class="pill" style="background:rgba(122,28,28,0.16); border-color:var(--c-oxblood); color:var(--c-oxblood);" title="The Shade's wound — every battle stat is cut in combat until it heals.">🩸 Shade-wound −${d.wound.pct}% · heals in ${healsIn(d.wound.healsAt)}</span>` : ''}
        ${membershipPillHtml(d.account)}
      </div>
      ${xpProgressHtml(c)}
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); margin-top: var(--s-2); font-size: 0.9rem;">
        <span class="pill gold" title="Your whole fortune: wallet, bank, and the reckoned value of everything you own — the same measure the Annals use.">${d.career ? `${formatCrowns(d.career.netWorth)} net worth` : `${formatCrowns(c.crowns)} crowns`}</span>
        <span class="pill">${c.tokens} tokens</span>
        <span class="pill">${c.boons} boons</span>
      </div>
      ${
        c.pinnedAwardSlugs && c.pinnedAwardSlugs.length > 0
          ? `<div style="display:flex; flex-wrap:wrap; gap: var(--s-2); margin-top: var(--s-3);">
              <span class="caption" style="align-self:center; margin-right: var(--s-2);">Pinned awards:</span>
              ${c.pinnedAwardSlugs
                .map((slug) => {
                  const a = d.awards.find((x) => x.slug === slug);
                  return a
                    ? `<span class="pill success" title="${escapeHtml(a.blurb)}">★ ${escapeHtml(a.name)}</span>`
                    : '';
                })
                .join('')}
            </div>`
          : ''
      }
    </section>

    <div class="profile-grid">
      <section class="panel">
        <h3 style="margin-top:0;">Battle Stats${woundPct > 0 ? ` <span style="font-size:0.75rem; color:var(--c-oxblood); font-weight:700;">🩸 −${woundPct}% wounded</span>` : ''}</h3>
        <table class="prof-stats">
          <tr><th>Strength</th><td>${battleStatCell(c.strength, woundPct)}</td></tr>
          <tr><th>Defense</th><td>${battleStatCell(c.defense, woundPct)}</td></tr>
          <tr><th>Speed</th><td>${battleStatCell(c.speed, woundPct)}</td></tr>
          <tr><th>Dexterity</th><td>${battleStatCell(c.dexterity, woundPct)}</td></tr>
          <tr class="total"><th>Total</th><td>${battleStatCell(battlePower, woundPct)}</td></tr>
        </table>
      </section>

      <section class="panel">
        <h3 style="margin-top:0;">Working Stats</h3>
        <table class="prof-stats">
          <tr><th>Toil</th><td>${formatStat(c.toil)}</td></tr>
          <tr><th>Wits</th><td>${formatStat(c.wits)}</td></tr>
          <tr><th>Endurance</th><td>${formatStat(c.endurance)}</td></tr>
          <tr class="total"><th>Total</th><td>${formatStat(workingTotal)}</td></tr>
        </table>
      </section>

      <section class="panel">
        <h3 style="margin-top:0;">Resources</h3>
        <table class="prof-stats">
          <tr><th>Vigor</th><td>${c.vigor} / ${c.vigorMax}</td></tr>
          <tr><th>Resolve</th><td>${c.resolve} / ${c.resolveMax}</td></tr>
          <tr><th>Spirit</th><td>${c.spirit} / ${c.spiritMax}</td></tr>
          <tr><th>Life</th><td>${c.life} / ${c.lifeMax}</td></tr>
        </table>
        <div class="refill-row">
          <div class="refill-pills">
            <div class="refill-pill-group">
              <button class="refill-pill" data-refill="vigor"
                ${c.vigor >= c.vigorMax || !canRefillBar(d, 'vigor') ? 'disabled' : ''}
                title="Spend 1 vigor refill token to top Vigor to ${c.vigorMax}">
                <span class="refill-pill-glyph vigor">⚔︎</span> Refill Vigor
              </button>
              <span class="refill-meta">×${c.vigorTokens}</span>
            </div>
            <div class="refill-pill-group">
              <button class="refill-pill" data-refill="resolve"
                ${c.resolve >= c.resolveMax || !canRefillBar(d, 'resolve') ? 'disabled' : ''}
                title="Spend 1 resolve refill token to top Resolve to ${c.resolveMax}">
                <span class="refill-pill-glyph resolve">⛨︎</span> Refill Resolve
              </button>
              <span class="refill-meta">×${c.resolveTokens}</span>
            </div>
            <div class="refill-pill-group">
              <button class="refill-pill" data-refill="spirit"
                ${c.spirit >= c.spiritMax || !canRefillBar(d, 'spirit') ? 'disabled' : ''}
                title="Spend 1 spirit refill token to top Spirit to ${c.spiritMax}">
                <span class="refill-pill-glyph spirit">✦</span> Refill Spirit
              </button>
              <span class="refill-meta">×${c.spiritTokens}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="panel">
        <h3 style="margin-top:0;">Reputation</h3>
        <table class="prof-stats">
          <tr><th>Infamy</th><td>${c.infamy.toLocaleString()}</td></tr>
          <tr><th>Cunning</th><td>${c.cunning.toLocaleString()}</td></tr>
          <tr><th>Virtue</th><td>${c.virtue.toLocaleString()}</td></tr>
          <tr><th>Honour</th><td>${c.honour.toLocaleString()}</td></tr>
          <tr><th>Awards</th><td>${achievedAwards} / ${totalAwards}</td></tr>
        </table>
      </section>

      <section class="panel">
        <h3 style="margin-top:0;">Crafts</h3>
        <table class="prof-stats">
          <tr>
            <th>Leechcraft</th>
            <td>
              ${craftProgress(c.leechcraftLevel).maxed
                ? `Level ${craftProgress(c.leechcraftLevel).level} · Mastered`
                : `Level ${craftProgress(c.leechcraftLevel).level} / 10`}
            </td>
          </tr>
          <tr>
            <th>&nbsp;&nbsp;<span style="color: var(--c-ink-soft); font-weight: normal;">Souls Revived</span></th>
            <td><span style="font-family: var(--font-numeric); color: var(--c-ink-soft);">${c.leechcraftLevel.toLocaleString()}</span></td>
          </tr>
          <tr>
            <th>Gaolcraft</th>
            <td>
              ${craftProgress(c.gaolcraftLevel).maxed
                ? `Level ${craftProgress(c.gaolcraftLevel).level} · Mastered`
                : `Level ${craftProgress(c.gaolcraftLevel).level} / 10`}
            </td>
          </tr>
          <tr>
            <th>&nbsp;&nbsp;<span style="color: var(--c-ink-soft); font-weight: normal;">Souls Freed</span></th>
            <td><span style="font-family: var(--font-numeric); color: var(--c-ink-soft);">${c.gaolcraftLevel.toLocaleString()}</span></td>
          </tr>
        </table>
      </section>
    </div>

    ${d.career ? renderCareer(d.career) : ''}

    <h2 class="prof-section-heading">Field Trades</h2>
    ${renderFieldTrades(d.gatheringSkills)}

    ${d.perks.length > 0 ? renderPerks(d.perks) : ''}

    ${d.activity.length > 0 ? renderActivity(d.activity) : ''}

    <h2 class="prof-section-heading">Equipment</h2>
    <div id="prof-loadout">${renderLoadout(d.loadout, d.inventory)}</div>

    <h2 class="prof-section-heading">House</h2>
    ${renderHouseBlock(d.house)}

    <h2 class="prof-section-heading">Domicile</h2>
    ${renderDomicileBlock(d.domicile)}

    <h2 class="prof-section-heading">Ship</h2>
    ${renderShipBlock(d.activeShip)}
  `;

  const style = document.createElement('style');
  style.textContent = `
    .profile-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: var(--s-3);
      margin-top: var(--s-3);
    }
    .prof-section-heading {
      font-family: var(--font-display);
      color: var(--c-oxblood);
      border-bottom: 1px solid var(--c-rule);
      padding-bottom: var(--s-1);
      margin: var(--s-5) 0 var(--s-3) 0;
    }
    table.prof-stats {
      width: 100%;
      border-collapse: collapse;
      font-family: var(--font-numeric);
    }
    table.prof-stats th, table.prof-stats td {
      text-align: left;
      padding: 6px 0;
      border-bottom: 1px solid var(--c-rule-soft);
      font-weight: normal;
    }
    table.prof-stats th { color: var(--c-ink-soft); }
    table.prof-stats td { font-weight: 600; text-align: right; }
    table.prof-stats tr.total th { color: var(--c-oxblood); font-family: var(--font-display); }
    table.prof-stats tr.total td { color: var(--c-oxblood); }
    .pill {
      background: var(--c-parchment-dark);
      border: 1px solid var(--c-rule-soft);
      border-radius: 999px;
      padding: 2px 10px;
      font-family: var(--font-numeric);
      font-size: 0.85rem;
    }
    /* (gold pill styling lives in pages.css — don't override here) */
    .equip-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: var(--s-3);
    }
    .equip-card {
      background: var(--c-parchment);
      border: 1px solid var(--c-rule);
      border-radius: var(--radius);
      padding: var(--s-3);
    }
    /* Fade ONLY the slot label, not the whole card — the equip picker's popup
       lives inside the card, and fading the card made the open dropdown
       translucent + glitchy (opacity stacking context). */
    .equip-card.empty h4 { opacity: 0.55; }
    .equip-card h4 { margin: 0 0 4px 0; font-family: var(--font-display); color: var(--c-oxblood); }
    ${EQUIP_PICKER_CSS}

    /* Field Trades (gathering professions) */
    .trade-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: var(--s-3);
      margin-top: var(--s-3);
    }
    .trade-card {
      background: var(--c-parchment);
      border: 1px solid var(--c-rule);
      border-radius: var(--radius);
      padding: var(--s-3);
    }
    .trade-card-untried { opacity: 0.6; }
    .trade-head { display: flex; align-items: baseline; justify-content: space-between; gap: var(--s-2); }
    .trade-head h4 { margin: 0; font-family: var(--font-display); color: var(--c-oxblood); }
    .trade-level { font-weight: 700; color: var(--c-ink-soft); white-space: nowrap; }
    .trade-bar { height: 6px; border-radius: 3px; background: var(--c-rule); overflow: hidden; margin: 8px 0; }
    .trade-bar > span { display: block; height: 100%; background: var(--c-oxblood); }
    .trade-stat { font-size: 0.95em; }
    .trade-tool { font-size: 0.85em; color: var(--c-ink-soft); margin-top: 2px; }

    /* Per-bar refill pills (Resources panel). */
    .refill-row {
      margin-top: var(--s-3);
      padding-top: var(--s-2);
      border-top: 1px solid var(--c-rule-soft);
    }
    .refill-pills { display: flex; flex-wrap: wrap; gap: var(--s-3); }
    .refill-pill-group {
      display: inline-flex; align-items: center; gap: 6px;
    }
    .refill-meta {
      font-size: 0.8rem; color: var(--c-ink-soft);
      font-family: var(--font-numeric); white-space: nowrap;
    }
    .refill-meta .free { color: var(--c-oxblood); font-weight: 700; }
    .refill-pill {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 12px; min-height: 36px;
      background: linear-gradient(to bottom, rgba(255, 245, 220, 0.85), rgba(214, 195, 147, 0.6));
      border: 1px solid var(--c-rule-strong); border-radius: var(--radius);
      /* Light gradient in both themes — pin dark ink (var(--c-ink) flips). */
      cursor: pointer; font: inherit; font-size: 0.9rem; color: #1c1108;
      box-shadow: var(--emboss-light), 0 1px 2px rgba(28, 17, 8, 0.10);
      transition: filter 0.12s ease;
    }
    .refill-pill:hover:not(:disabled) { filter: brightness(1.05); }
    .refill-pill:disabled { opacity: 0.45; cursor: not-allowed; }
    .refill-pill-glyph { font-size: 1.05rem; line-height: 1; }
    .refill-pill-glyph.vigor { color: var(--c-vigor); }
    .refill-pill-glyph.resolve { color: var(--c-resolve); }
    .refill-pill-glyph.spirit { color: var(--c-spirit); }

    /* Scholarium Perks table — owned perks with legacy stacking progress. */
    table.perks-table { table-layout: fixed; }
    table.perks-table th, table.perks-table td { vertical-align: baseline; }
    /* The perk-name column inherits prof-stats' right-aligned td; force it left. */
    table.perks-table th:first-child, table.perks-table td:first-child { text-align: left; }
    table.perks-table .perk-cell {
      font-family: var(--font-display);
      color: var(--c-oxblood);
      text-align: left;
      overflow-wrap: anywhere;
    }
    table.perks-table .perk-kind {
      color: var(--c-ink-soft);
      font-family: var(--font-numeric);
      font-size: 0.8rem;
      text-align: left;
      white-space: nowrap;
    }
    table.perks-table .perk-prog {
      text-align: right;
      font-family: var(--font-numeric);
      white-space: nowrap;
    }
    table.perks-table .perk-prog.maxed { color: var(--c-oxblood); font-weight: 700; }
    @media (max-width: 480px) {
      /* Drop the middle "Type" column on narrow screens to avoid cramping. */
      table.perks-table .perk-kind { display: none; }
    }
  `;
  main.appendChild(style);

  // Make the lower section headings (Equipment / House / Domicile / Ship /
  // Recent activity) collapsible. The top stat panels have no such heading and
  // stay always-visible. State persists per-section in localStorage.
  autoCollapseSiblings(main, 'h2.prof-section-heading', 'profile');

  // Make every perk chip in the Scholarium Perks table open its info modal.
  main.querySelectorAll<HTMLElement>('.perk-cell[data-slug]').forEach((el) => {
    attachPerkInfoToggle(el, el.dataset.slug!);
  });

  // Wire the avatar button — opens the avatar library modal.
  main.querySelector<HTMLButtonElement>('#profile-avatar-btn')?.addEventListener('click', () => {
    openAvatarDialog(d.character);
  });

  // Wire the per-bar refill pills — each spends 1 refill token (or the free
  // daily membership refill) to top a single bar to max. A confirm dialog
  // guards against accidental clicks and tells the player how many tokens
  // they have left. On success we re-route to the same hash so the whole
  // Profile re-renders with fresh data.
  main.querySelectorAll<HTMLButtonElement>('button[data-refill]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (btn.disabled) return;
      const bar = btn.dataset.refill as 'vigor' | 'resolve' | 'spirit';
      const barLabel = bar === 'vigor' ? 'Vigor' : bar === 'resolve' ? 'Resolve' : 'Spirit';
      // Per-bar bucket: pull from refillStatus when available, fall back to
      // the character row's per-bar field.
      const tokensLeft =
        d.refillStatus?.[bar].tokens ??
        (bar === 'vigor' ? d.character.vigorTokens
          : bar === 'resolve' ? d.character.resolveTokens
          : d.character.spiritTokens);
      const tokenWord = tokensLeft === 1 ? 'token' : 'tokens';
      // Refill SETS the bar to max — it doesn't add. So any amount you
      // already have in the bar is effectively forfeited. Warn the player
      // about that so they don't burn a token when they could've drained the
      // bar first for full value.
      const current =
        bar === 'vigor' ? d.character.vigor :
        bar === 'resolve' ? d.character.resolve : d.character.spirit;
      const max =
        bar === 'vigor' ? d.character.vigorMax :
        bar === 'resolve' ? d.character.resolveMax : d.character.spiritMax;
      const gain = Math.max(0, max - current);
      const forfeit = current; // the amount of the bar you give up
      const costLine = `Spends 1 ${barLabel.toLowerCase()} refill token. You have ${tokensLeft} ${tokenWord} left in that bucket.`;
      const refillLine = `You're at ${current} / ${max} ${barLabel}. This tops you to ${max} (gain +${gain}).`;
      // Surface the forfeit cost clearly. If you've got a non-trivial chunk
      // of the bar left, tell the player they'd be wasting it — they can
      // cancel and spend it down first for the full value of the refill.
      const wastePct = max > 0 ? (forfeit / max) * 100 : 0;
      const wasteLine =
        forfeit === 0
          ? ''
          : wastePct >= 25
            ? `⚠ You'll forfeit ${forfeit} ${barLabel} you already have. Consider spending it first.`
            : `You'll forfeit ${forfeit} ${barLabel} you already have.`;
      const message = [refillLine, wasteLine, costLine].filter(Boolean).join('\n\n');
      const ok = await showConfirmDialog({
        title: `Refill ${barLabel}?`,
        message,
        confirmLabel: 'Yes, refill',
        cancelLabel: 'Cancel',
        tone: 'gold',
      });
      if (!ok) return;
      btn.disabled = true;
      try {
        const r = await api.useRefill(bar);
        store.set({ character: r.character });
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (e) {
        const msg = e instanceof HttpError ? e.message : 'Refill failed.';
        // Surface the error as a parchment-styled banner (consistent with the
        // rest of the app) rather than a native browser alert.
        const err = document.createElement('div');
        err.className = 'alert danger';
        err.style.marginBottom = 'var(--s-3)';
        err.textContent = msg;
        main.prepend(err);
        err.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        window.setTimeout(() => err.remove(), 6000);
        btn.disabled = false;
      }
    });
  });

  // Wire the per-slot equip pickers. Choosing an item equips (or unequips for
  // "— none —"). Rather than re-routing the hash (which hard-refreshes the whole
  // Profile), we re-fetch just the loadout + inventory and swap the loadout
  // section in place, leaving the rest of the page and the scroll position be.
  const onProfileSlotChange = async (result: SlotChangeResult): Promise<void> => {
    flashToast(result);
    if (result.kind !== 'success') return;
    const [loadoutR, invR] = await Promise.allSettled([api.getLoadout(), api.listInventory()]);
    if (loadoutR.status === 'fulfilled') d.loadout = loadoutR.value.loadout;
    if (invR.status === 'fulfilled') d.inventory = invR.value.items;
    // Equipping can shift derived totals; pull the fresh character into the store.
    try { const me = await api.me(); store.set({ character: me.character }); d.character = me.character; } catch { /* keep stale */ }
    const host = main.querySelector('#prof-loadout');
    if (host) {
      host.innerHTML = renderLoadout(d.loadout, d.inventory);
      wireSlotPickers(host as HTMLElement, onProfileSlotChange);
    } else {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
  };
  wireSlotPickers(main, onProfileSlotChange);
}

/** True when the player has a refill token in THIS bar's bucket. Membership
 *  bonus tokens are deposited straight into the buckets, so they're already
 *  reflected in the per-bar count — no separate free-refill path. */
function canRefillBar(d: ProfileData, bar: 'vigor' | 'resolve' | 'spirit'): boolean {
  if (d.refillStatus) return d.refillStatus[bar].tokens > 0;
  // No refillStatus loaded — fall back to the per-bar count on the character row.
  const ct =
    bar === 'vigor' ? d.character.vigorTokens
    : bar === 'resolve' ? d.character.resolveTokens
    : d.character.spiritTokens;
  return ct > 0;
}

/** Field Trades panel — every gathering profession with level, progress and a
 *  lifetime tally. Always shows all eight so players can see the trades exist
 *  and go find the ground for them out in the realm. */
function renderFieldTrades(skills: GatheringSkillSummaryDto[]): string {
  if (skills.length === 0) {
    return `<section class="panel"><p style="font-style:italic; color: var(--c-ink-soft);">The field trades could not be loaded just now.</p></section>`;
  }
  const cards = skills
    .map((s) => {
      const total = Number(s.totalCatch);
      const pct = s.xpForNextLevel && s.xpForNextLevel > 0
        ? Math.min(100, Math.round((s.xpIntoLevel / s.xpForNextLevel) * 100))
        : 100;
      const progressLabel = s.xpForNextLevel
        ? `${s.xpIntoLevel.toLocaleString()} / ${s.xpForNextLevel.toLocaleString()} XP`
        : 'Fully mastered';
      const toolLine = !s.hasToolSlot
        ? 'Bare-handed trade'
        : s.toolEquipped
          ? `${escapeHtml(s.toolEquipped.name)} (power ${s.toolEquipped.power})`
          : 'No tool equipped';
      const started = total > 0 || s.level > 1;
      return `
        <div class="trade-card${started ? '' : ' trade-card-untried'}">
          <div class="trade-head">
            <h4>${escapeHtml(s.name)}</h4>
            <span class="trade-level">Lv ${s.level}</span>
          </div>
          <div class="trade-bar" title="${progressLabel}"><span style="width:${pct}%;"></span></div>
          <div class="trade-stat"><strong>${total.toLocaleString()}</strong> ${escapeHtml(s.yieldNoun)}</div>
          <div class="trade-tool">${toolLine}</div>
        </div>`;
    })
    .join('');
  return `
    <section class="panel">
      <p style="font-style:italic; color: var(--c-ink-soft); margin-top:0;">Eight trades work the realm's coasts, forests, mountains and rivers. Travel to the right ground to take one up — each levels on its own.</p>
      <div class="trade-grid">${cards}</div>
    </section>`;
}

function renderLoadout(l: LoadoutDto | null, inventory: InventoryItemDto[]): string {
  if (!l) {
    return `<section class="panel"><p style="font-style:italic; color: var(--c-ink-soft);">No loadout — visit Inventory to equip arms and armour.</p></section>`;
  }
  type SlotKey = 'primary' | 'secondary' | 'helm' | 'chest' | 'amulet' | 'rod' | 'spear' | 'pickaxe' | 'axe' | 'basket';
  const slots: Array<{ name: string; key: SlotKey; slot: typeof l.primary }> = [
    { name: 'Primary',   key: 'primary',   slot: l.primary },
    { name: 'Secondary', key: 'secondary', slot: l.secondary },
    { name: 'Helm',      key: 'helm',      slot: l.helm },
    { name: 'Chest',     key: 'chest',     slot: l.chest },
    { name: 'Amulet',    key: 'amulet',    slot: l.amulet },
  ];
  // All five tool slots always show (empty if no tool owned), so players can see
  // the gathering professions exist and go find the tools out in the realm.
  const tools: Array<{ name: string; key: SlotKey; slot: typeof l.primary }> = [
    { name: 'Fishing Rod',    key: 'rod',     slot: l.rod },
    { name: 'Hunting Spear',  key: 'spear',   slot: l.spear },
    { name: 'Pickaxe',        key: 'pickaxe', slot: l.pickaxe },
    { name: 'Axe',            key: 'axe',     slot: l.axe },
    { name: 'Foraging Basket',key: 'basket',  slot: l.basket },
  ];
  for (const t of tools) slots.push({ name: t.name, key: t.key, slot: t.slot });
  // Each slot is a dropdown of the eligible items you already own, so you can
  // swap gear here without hopping to the Inventory page.
  return `
    <div class="equip-grid">
      ${slots
        .map((s) => `
          <div class="equip-card">
            <h4>${s.name}</h4>
            ${slotPickerHtml(s.key, s.slot, inventory)}
          </div>
        `).join('')}
    </div>
    <section class="panel" style="margin-top: var(--s-3);">
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3);">
        <span class="pill">Total weapon attack: <strong>${l.totalWeaponAttack}</strong></span>
        <span class="pill">Total weapon accuracy: <strong>${l.totalWeaponAccuracy}</strong></span>
        <span class="pill">Total armour: <strong>${l.totalArmor}</strong></span>
      </div>
    </section>
  `;
}

function renderHouseBlock(h: HouseDetailDto | null): string {
  if (!h) {
    return `<section class="panel"><p style="font-style:italic; color: var(--c-ink-soft);">Unaligned — no House.</p></section>`;
  }
  return `
    <section class="panel">
      <div style="display:flex; flex-wrap:wrap; align-items: baseline; gap: var(--s-3);">
        <h3 style="margin:0;">${escapeHtml(h.name)} <span class="pill">[${escapeHtml(h.tag)}]</span></h3>
        ${h.myRank ? `<span class="pill gold">Your rank: <strong>${h.myRank}</strong></span>` : ''}
      </div>
      ${h.motto ? `<p style="font-style:italic; color: var(--c-ink-soft); margin-top: var(--s-2);">"${escapeHtml(h.motto)}"</p>` : ''}
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); margin-top: var(--s-2); font-size: 0.9rem;">
        <span class="pill">Members ${h.memberCount}</span>
        <span class="pill">Renown ${h.renown}</span>
        <span class="pill gold">Treasury ${formatCrowns(h.treasuryCrowns)} crowns</span>
      </div>
    </section>
  `;
}

function renderDomicileBlock(d: OwnedDomicileDto | null): string {
  if (!d) {
    return `<section class="panel"><p style="font-style:italic; color: var(--c-ink-soft);">No domicile — visit Domiciles to acquire one.</p></section>`;
  }
  return `
    <section class="panel">
      <div style="display:flex; flex-wrap:wrap; align-items: baseline; gap: var(--s-3);">
        <h3 style="margin:0;">${escapeHtml(d.tierName)}</h3>
        <span class="pill">+${d.spiritBonus} spirit</span>
        <span class="pill">${d.slotsUsed} / ${d.upgradeSlots} upgrade slots</span>
      </div>
      ${d.upgrades.length > 0
        ? `<div style="display:flex; flex-wrap:wrap; gap: 6px; margin-top: var(--s-2);">
             ${d.upgrades.map((u) => `<span class="pill">${escapeHtml(u.name)}</span>`).join('')}
           </div>`
        : `<p style="font-style:italic; color: var(--c-ink-soft); margin: var(--s-2) 0 0 0;">No upgrades installed.</p>`}
      <div style="font-size: 0.85rem; color: var(--c-ink-soft); margin-top: var(--s-2);">
        Acquired ${new Date(d.acquiredAt).toLocaleDateString()}
      </div>
    </section>
  `;
}

function renderShipBlock(m: ShipDto | null): string {
  if (!m) {
    return `<section class="panel"><p style="font-style:italic; color: var(--c-ink-soft);">Sailing the Coracle. Visit the Royal Shipyard on The Roads to acquire a finer ship.</p></section>`;
  }
  return `
    <section class="panel">
      <div style="display:flex; flex-wrap:wrap; align-items: baseline; gap: var(--s-3);">
        <h3 style="margin:0;">${escapeHtml(m.name)}</h3>
        <span class="pill gold">Active</span>
        <span class="pill">${m.speedMultiplier.toFixed(2)}× travel speed</span>
      </div>
      <p style="font-style:italic; color: var(--c-ink-soft); margin: var(--s-2) 0 0 0;">${escapeHtml(m.blurb)}</p>
    </section>
  `;
}

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

function formatStat(n: number): string {
  // Big stat values get suffix treatment so we don't blow out the layout
  // once a character climbs into the late game (trillions and beyond).
  // Smaller values keep precision-friendly comma separators.
  if (Math.abs(n) >= 1_000_000) return formatBigNum(n);
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
}

/** Progress-to-next-level bar. Uses the server-supplied xpIntoLevel /
 *  xpToNextLevel (BigInt-as-string) so the client needn't know the XP curve. */
function xpProgressHtml(c: CharacterDto): string {
  let into: bigint;
  let toNext: bigint;
  try {
    into = BigInt(c.xpIntoLevel);
    toNext = BigInt(c.xpToNextLevel);
  } catch {
    return '';
  }
  const span = into + toNext;
  // basis-points → percent, computed in BigInt to stay exact at any level.
  const pct = span > 0n ? Number((into * 10_000n) / span) / 100 : 100;
  const label =
    toNext > 0n
      ? `${formatBigStr(c.xpIntoLevel)} / ${formatBigStr(span.toString())} XP · ${formatBigStr(c.xpToNextLevel)} to Level ${c.level + 1}`
      : 'Highest XP band reached';
  return `
    <div class="xp-progress" title="${label}" style="margin-top: var(--s-3);">
      <div class="xp-progress-track">
        <div class="xp-progress-fill" style="width: ${pct.toFixed(1)}%;"></div>
      </div>
      <div class="xp-progress-label caption">${label}</div>
    </div>`;
}

function formatBigStr(s: string): string {
  // Crown totals as BigInt-safe strings. Apply suffix formatting once we get
  // past a million — keeps the Profile page readable at any wealth level.
  if (s && /^-?\d+$/.test(s)) {
    const raw = s.startsWith('-') ? s.slice(1) : s;
    if (raw.length > 6) return formatBigStrShared(s);
  }
  // Smaller: full comma-separated value for precision.
  const negative = s.startsWith('-');
  const raw = negative ? s.slice(1) : s;
  const parts: string[] = [];
  for (let i = raw.length; i > 0; i -= 3) {
    parts.unshift(raw.slice(Math.max(0, i - 3), i));
  }
  return (negative ? '-' : '') + parts.join(',');
}

function formatLocation(loc: string): string {
  return loc
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Returns a styled pill showing the player's membership tier (Squire / Knight
 * / Baron) tinted with their flair colour, or an empty string for free
 * accounts. Sits in the Profile header pill row.
 */
function membershipPillHtml(account: AccountSummary): string {
  const tier = account.membershipTier;
  const flair = account.membershipFlairColour;
  if (!tier || !flair) return '';
  const tierName = tier.charAt(0).toUpperCase() + tier.slice(1);
  // Border + faint background tint in the flair colour so the pill reads as a
  // status badge, not just another grey pill. The text itself uses the flair
  // colour with the same 1px black outline as the name above.
  return `<span class="pill" style="border-color: ${flair}; background: ${flair}1f; color: ${flair}; font-weight: 700; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000;">${escapeHtml(tierName)}</span>`;
}

/**
 * Profile identity block — avatar on the left, stacked name/rank/title lines
 * on the right. Replaces the old single-line page-character-name div.
 *
 * Click the avatar to open the avatar dialog (managed by Profile's click
 * delegation — see the avatar wiring near renderProfilePanels).
 */
function renderIdentityBlock(c: CharacterDto, flair: string | null): string {
  // Resolve human-readable rank + sub-title text via the client Title mirror.
  const rankDef = getTitleDef(c.levelRankSlug);
  const titleDef = getTitleDef(c.activeTitleSlug);
  const rankText = rankDef?.text ?? '—';

  // The avatar block — either the active image or the heraldic default
  // (painterly hooded peasant; 512x512 PNG resized down to 128 on display).
  const avatarSrc = c.activeAvatarUrl ?? '/img/avatar-default.webp';
  const avatarHtml = `
    <button class="profile-avatar" id="profile-avatar-btn"
      type="button"
      aria-label="Change profile photo"
      title="Click to manage your avatars">
      <span class="profile-avatar-frame">
        <img class="profile-avatar-img" src="${escapeHtml(avatarSrc)}" alt="Your avatar" />
        <span class="profile-avatar-edit">Change</span>
      </span>
    </button>`;

  // Tier-tinted values — mirrors the chip palette without needing chip wrappers.
  const tierColor = (tier?: string): string => {
    switch (tier) {
      case 'ascended':  return '#ff3030';
      case 'mythic':    return '#c2369c';
      case 'legendary': return 'var(--c-gold-bright)';
      case 'rare':      return 'var(--c-oxblood)';
      default:          return 'var(--c-ink)';
    }
  };
  const rankColor = tierColor(rankDef?.tier);
  const titleColor = tierColor(titleDef?.tier);

  const nameSpan = flair
    ? `<span class="player-name-flair" style="color: ${flair}; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 10px ${flair}88;">${escapeHtml(c.name)}</span>`
    : escapeHtml(c.name);

  // Title row — when no sub-title is chosen, offer a route to Settings instead
  // of the dead "— None —" placeholder. Bestowed titles can carry bespoke
  // colour overrides (e.g. Kaan's blue outline, the founder gold→crimson
  // gradient) via customTitleCss; everything else uses its tier colour.
  // Marble-pill titles (founder, Incorruptible, etc.) render as their full
  // chip here so the pill's dark/light fill backs the text — bare coloured
  // text would be illegible on parchment for the light-fill ones.
  const titleStyle = customTitleCss(titleDef?.slug) ?? `color: ${titleColor};`;
  const titleValue = titleDef
    ? (customChipCss(titleDef.slug)
        ? `<span class="identity-value">${titleChipHtml(titleDef.slug)}</span>`
        : `<span class="identity-value" style="${titleStyle}">${escapeHtml(titleDef.text)}</span>`)
    : `<a class="identity-value identity-value-empty" href="#/settings" title="Pick one from your earned titles">— Choose in Settings →</a>`;

  return `
    <div class="identity-block">
      ${avatarHtml}
      <div class="identity-text">
        <div class="identity-name">${nameSpan}</div>
        <hr class="identity-rule" />
        <div class="identity-row">
          <span class="identity-label">RANK</span>
          <span class="identity-value" style="color: ${rankColor};">${escapeHtml(rankText)}</span>
        </div>
        <div class="identity-row">
          <span class="identity-label">TITLE</span>
          ${titleValue}
        </div>
      </div>
    </div>`;
}

function renderActivity(entries: ActivityEntryDto[]): string {
  const iconOf = (k: string): string => {
    switch (k) {
      case 'crime': return '☠';
      case 'attack_initiated': return '⚔';
      case 'attack_received': return '⛨';
      case 'market_sold': return '⛁';
      case 'market_bought': return '🜨';
      case 'award_earned': return '⚜';
      case 'expedition_claimed': return '✦';
      default: return '◆';
    }
  };
  const relTime = (iso: string): string => {
    const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (sec < 60) return 'just now';
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  };

  return `
    <h2 class="prof-section-heading">Recent activity</h2>
    <section class="panel activity-panel">
      <ul class="activity-feed">
        ${entries.map((e) => `
          <li class="activity-row">
            <span class="activity-icon" aria-hidden="true">${iconOf(e.kind)}</span>
            <div class="activity-body">
              <div class="activity-msg">${escapeHtml(e.message)}</div>
              ${e.detail ? `<div class="activity-detail">${escapeHtml(e.detail)}</div>` : ''}
            </div>
            <span class="activity-when">${relTime(e.at)}</span>
          </li>
        `).join('')}
      </ul>
    </section>
    <style>
      .activity-feed { list-style: none; margin: 0; padding: 0; }
      .activity-row {
        display: flex;
        gap: var(--s-3);
        padding: 8px 0;
        border-bottom: 1px solid var(--c-rule-soft);
        align-items: flex-start;
      }
      .activity-row:last-child { border-bottom: 0; }
      .activity-icon {
        flex: 0 0 auto;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--c-parchment-dark);
        border: 1px solid var(--c-rule);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--c-oxblood);
        font-size: 0.95rem;
      }
      .activity-body { flex: 1 1 auto; min-width: 0; }
      .activity-msg { font-family: var(--font-body); color: var(--c-ink); font-size: 0.95rem; }
      .activity-detail { font-family: var(--font-elegant); font-style: italic; font-size: 0.92rem; color: var(--c-ink-soft); }
      .activity-when {
        flex: 0 0 auto;
        font-family: var(--font-display);
        font-size: 0.88rem;
        color: var(--c-ink-soft);
        white-space: nowrap;
      }
    </style>
  `;
}

function renderPerks(perks: PerkProgressDto[]): string {
  // Legacy perks first (they carry progress), then one-time unlocks; each group
  // alphabetised by display name for a stable, readable order.
  const sorted = [...perks].sort((a, b) => {
    if (a.legacy !== b.legacy) return a.legacy ? -1 : 1;
    return perkName(a.slug).localeCompare(perkName(b.slug));
  });

  const rows = sorted
    .map((p) => {
      const name = escapeHtml(perkName(p.slug));
      const known = !!PERK_CATALOG[p.slug];
      // Clickable chip only when we have catalog info to show in the modal.
      const nameCell = known
        ? `<span class="perk-cell" data-slug="${escapeHtml(p.slug)}">${name}</span>`
        : `<span class="perk-cell-plain">${name}</span>`;
      const kind = p.legacy ? 'Legacy' : 'One-time';
      const maxed = p.legacy && p.completions >= p.max;
      const prog = p.legacy
        ? `${p.completions}/${p.max}${maxed ? ' ✦' : ''}`
        : 'Unlocked';
      return `<tr>
        <td>${nameCell}</td>
        <td class="perk-kind">${kind}</td>
        <td class="perk-prog ${maxed ? 'maxed' : ''}">${prog}</td>
      </tr>`;
    })
    .join('');

  return `
    <h2 class="prof-section-heading">Scholarium Perks</h2>
    <section class="panel">
      <p class="caption" style="margin-top:0;">
        Perks earned at the Scholarium. Legacy perks raise a stat cap each time you
        complete their course, up to 5 completions — tap a perk to read its effect.
      </p>
      <table class="prof-stats perks-table">
        <tr><th>Perk</th><th class="perk-kind">Type</th><th class="perk-prog">Progress</th></tr>
        ${rows}
      </table>
    </section>
  `;
}

function renderCareer(s: CareerStatsDto): string {
  const successRate = s.crimes.attempts > 0
    ? Math.round(((s.crimes.triumphs + s.crimes.partials) / s.crimes.attempts) * 100)
    : 0;
  const winRate = s.combat.fightsInitiated > 0
    ? Math.round((s.combat.wins / s.combat.fightsInitiated) * 100)
    : 0;

  return `
    <h2 class="prof-section-heading">Career</h2>
    <div class="profile-grid">
      <section class="panel">
        <h3 style="margin-top:0;">Misdeeds</h3>
        <table class="prof-stats">
          <tr><th>Attempts</th><td>${s.crimes.attempts.toLocaleString()}</td></tr>
          <tr><th>Triumphs</th><td>${s.crimes.triumphs.toLocaleString()}</td></tr>
          <tr><th>Partial</th><td>${s.crimes.partials.toLocaleString()}</td></tr>
          <tr><th>Foiled</th><td>${s.crimes.foiled.toLocaleString()}</td></tr>
          <tr><th>Disastrous</th><td>${s.crimes.disastrous.toLocaleString()}</td></tr>
          <tr class="total"><th>Success rate</th><td>${successRate}%</td></tr>
          <tr><th>Crowns earned</th><td>${formatCrowns(s.crimes.crownsEarned)}</td></tr>
          <tr><th>Infamy earned</th><td>${formatBigNum(s.crimes.infamyEarned)}</td></tr>
        </table>
      </section>

      <section class="panel">
        <h3 style="margin-top:0;">Good Deeds</h3>
        <table class="prof-stats">
          <tr><th>Attempts</th><td>${s.goodDeeds.attempts.toLocaleString()}</td></tr>
          <tr><th>Triumphs</th><td>${s.goodDeeds.triumphs.toLocaleString()}</td></tr>
          <tr><th>Partial</th><td>${s.goodDeeds.partials.toLocaleString()}</td></tr>
          <tr><th>Unfulfilled</th><td>${s.goodDeeds.unfulfilled.toLocaleString()}</td></tr>
          <tr class="total"><th>Success rate</th><td>${
            s.goodDeeds.attempts > 0
              ? Math.round(((s.goodDeeds.triumphs + s.goodDeeds.partials) / s.goodDeeds.attempts) * 100)
              : 0
          }%</td></tr>
          <tr><th>Crowns earned</th><td>${formatCrowns(s.goodDeeds.crownsEarned)}</td></tr>
          <tr><th>Honour earned</th><td>${formatBigNum(s.goodDeeds.honourEarned)}</td></tr>
        </table>
      </section>

      <section class="panel">
        <h3 style="margin-top:0;">Combat</h3>
        <table class="prof-stats">
          <tr><th>Fights</th><td>${s.combat.fightsInitiated.toLocaleString()}</td></tr>
          <tr><th>Wins</th><td>${s.combat.wins.toLocaleString()}</td></tr>
          <tr><th>Losses</th><td>${s.combat.losses.toLocaleString()}</td></tr>
          <tr><th>Draws</th><td>${s.combat.draws.toLocaleString()}</td></tr>
          <tr><th>Escaped</th><td>${s.combat.escaped.toLocaleString()}</td></tr>
          <tr class="total"><th>Win rate</th><td>${winRate}%</td></tr>
          <tr><th>Damage dealt</th><td>${s.combat.totalDamageDealt.toLocaleString()}</td></tr>
          <tr><th>XP from combat</th><td>${formatBigNum(s.combat.xpFromCombat)}</td></tr>
        </table>
      </section>

      <section class="panel">
        <h3 style="margin-top:0;">Merchanting</h3>
        <table class="prof-stats">
          <tr><th>Items sold</th><td>${s.market.itemsSold.toLocaleString()}</td></tr>
          <tr><th>Items bought</th><td>${s.market.itemsBought.toLocaleString()}</td></tr>
          <tr><th>Crowns earned from sales</th><td>${formatCrowns(s.market.crownsEarnedFromSales)}</td></tr>
          <tr><th>Crowns paid in fees</th><td>${formatCrowns(s.market.crownsPaidInFees)}</td></tr>
          <tr><th>Crowns spent buying</th><td>${formatCrowns(s.market.crownsSpentBuying)}</td></tr>
        </table>
      </section>

      <section class="panel">
        <h3 style="margin-top:0;">Honours</h3>
        <table class="prof-stats">
          <tr><th>Awards earned</th><td>${s.awardsCount.toLocaleString()}</td></tr>
          <tr><th>Expeditions claimed</th><td>${s.expeditionsClaimed.toLocaleString()}</td></tr>
        </table>
      </section>
    </div>
  `;
}

// ─── Avatar dialog ──────────────────────────────────────────────────────────
// Opens a centred modal that shows the player's avatar library, an Upload
// button (resizes to 256x256 client-side before sending), and a Delete on
// each slot. Selecting one makes it active; closing the modal re-renders
// the Profile by re-routing to the same hash so the new avatar shows.

const AVATAR_MAX_SLOTS = 5;
const AVATAR_RENDER_SIZE = 256;
const AVATAR_ACCEPTED_MIMES = 'image/jpeg,image/png,image/webp';
const AVATAR_MAX_SOURCE_BYTES = 5 * 1024 * 1024; // 5 MB raw source cap

/**
 * Resize a user-picked image File to a 256x256 JPEG data URL using a canvas.
 * Centre-cropped to a square so non-square inputs don't get squashed.
 * Returns null if the file is unreadable.
 */
async function resizeToAvatarDataUrl(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = AVATAR_RENDER_SIZE;
        canvas.height = AVATAR_RENDER_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_RENDER_SIZE, AVATAR_RENDER_SIZE);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

function openAvatarDialog(initialCharacter: CharacterDto): void {
  // Snapshot of the library — refreshed in place as the user uploads/deletes.
  let library = [...initialCharacter.avatarLibrary];
  let active = initialCharacter.activeAvatarUrl;

  const overlay = document.createElement('div');
  overlay.className = 'avatar-dialog-overlay';
  overlay.innerHTML = `
    <div class="avatar-dialog" role="dialog" aria-labelledby="avatar-dialog-title">
      <h2 id="avatar-dialog-title">Profile Photo</h2>
      <p class="avatar-dialog-instructions">
        Upload up to <strong>5</strong> photos. Recommended size: <strong>512&times;512</strong> square.
        Max file size <strong>5 MB</strong>. JPG, PNG, or WebP. Images are automatically cropped to a
        square and resized to 256&times;256 before upload.
      </p>
      <div class="avatar-grid" id="avatar-grid"></div>
      <div class="avatar-dialog-actions">
        <input type="file" id="avatar-upload-input" accept="${AVATAR_ACCEPTED_MIMES}" hidden />
        <button class="btn" id="avatar-upload-btn">Upload new photo</button>
        <button class="btn-secondary" id="avatar-close-btn">Close</button>
      </div>
      <div class="avatar-dialog-status" id="avatar-status" role="status"></div>
    </div>`;
  document.body.appendChild(overlay);

  const gridEl = overlay.querySelector<HTMLDivElement>('#avatar-grid')!;
  const statusEl = overlay.querySelector<HTMLDivElement>('#avatar-status')!;
  const uploadBtn = overlay.querySelector<HTMLButtonElement>('#avatar-upload-btn')!;
  const fileInput = overlay.querySelector<HTMLInputElement>('#avatar-upload-input')!;
  const closeBtn = overlay.querySelector<HTMLButtonElement>('#avatar-close-btn')!;

  function setStatus(msg: string, kind: 'info' | 'error' | 'success' = 'info'): void {
    statusEl.textContent = msg;
    statusEl.className = `avatar-dialog-status status-${kind}`;
  }

  function renderGrid(): void {
    const slotsUsed = library.length;
    const slotsLeft = AVATAR_MAX_SLOTS - slotsUsed;
    const slotsHtml = library.map((url) => `
      <div class="avatar-slot ${url === active ? 'is-active' : ''}" data-url="${escapeHtml(url)}">
        <img src="${escapeHtml(url)}" alt="Avatar" />
        <div class="avatar-slot-actions">
          ${url === active
            ? '<span class="avatar-active-label">Active</span>'
            : `<button class="btn-tiny" data-action="select">Use</button>`}
          <button class="btn-tiny danger" data-action="delete">Delete</button>
        </div>
      </div>`).join('');
    const emptyHtml = Array.from({ length: slotsLeft }, () =>
      `<div class="avatar-slot is-empty">+</div>`).join('');
    gridEl.innerHTML = slotsHtml + emptyHtml;
    uploadBtn.disabled = slotsLeft <= 0;
    uploadBtn.textContent = slotsLeft > 0
      ? `Upload new photo (${slotsLeft} slot${slotsLeft === 1 ? '' : 's'} free)`
      : 'Library full — delete one to upload';
  }

  // Delegate clicks for the per-slot Use/Delete buttons.
  gridEl.addEventListener('click', async (ev) => {
    const t = ev.target as HTMLElement;
    const btn = t.closest('button[data-action]') as HTMLButtonElement | null;
    if (!btn) return;
    const slot = btn.closest('.avatar-slot') as HTMLElement | null;
    const url = slot?.dataset.url;
    if (!url) return;
    btn.disabled = true;
    try {
      if (btn.dataset.action === 'select') {
        const r = await api.selectAvatar(url);
        library = r.library.avatarLibrary;
        active = r.library.activeAvatarUrl;
        setStatus('Active avatar updated.', 'success');
      } else if (btn.dataset.action === 'delete') {
        const r = await api.deleteAvatar(url);
        library = r.library.avatarLibrary;
        active = r.library.activeAvatarUrl;
        setStatus('Avatar deleted — slot freed.', 'success');
      }
      renderGrid();
    } catch (e) {
      const msg = e instanceof HttpError ? e.message : 'Action failed.';
      setStatus(msg, 'error');
      btn.disabled = false;
    }
  });

  uploadBtn.addEventListener('click', () => fileInput.click());

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    fileInput.value = ''; // allow re-picking the same file
    if (!file) return;
    if (file.size > AVATAR_MAX_SOURCE_BYTES) {
      setStatus(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`, 'error');
      return;
    }
    setStatus('Resizing image…');
    const dataUrl = await resizeToAvatarDataUrl(file);
    if (!dataUrl) {
      setStatus('Could not read that file — try a different image.', 'error');
      return;
    }
    setStatus('Uploading…');
    try {
      const r = await api.uploadAvatar(dataUrl);
      library = r.library.avatarLibrary;
      active = r.library.activeAvatarUrl;
      setStatus('Uploaded. Click "Use" to switch your active avatar.', 'success');
      renderGrid();
    } catch (e) {
      const msg = e instanceof HttpError ? e.message : 'Upload failed.';
      setStatus(msg, 'error');
    }
  });

  closeBtn.addEventListener('click', () => {
    document.body.removeChild(overlay);
    // Re-route to the same hash so the Profile re-renders with the new avatar
    // visible. Cheaper than a wholesale reload.
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  });

  // Initial render.
  renderGrid();
}